import styled from '@emotion/styled';
import { Box, Text } from '@mantine/core';
import { Picker } from '@root/Components/Picker/Picker';
import { FillerSwitch } from '@root/Design/Filler';
import { EllipsisTextEl } from '@root/Design/Text';
import { UserRecord } from '@root/Services/Customers/CompanyUserService';
import { ComponentPropsWithoutRef, useCallback, useEffect, useMemo, useState } from 'react';
import { useFloatedFullText } from '../Text/FloatedFullText';
import { useCompanyUserList, useCurrentUser } from './UserHooks';
import { UserImage } from './UserImage';
import { SortAscending2, SortAscendingLetters, SortDescending2, SortDescendingLetters } from 'tabler-icons-react';
import { OptionMenuButton } from '../Picker/OptionMenu';
import { useGlobalUserPrefs } from '@root/Services/Customers/UserPreferenceService';

interface IUserPickerProps {
    selectedUserIds: number[];
    onChange: (userIds: number[]) => void;
    excludeCurrentUser?: boolean;
    includeDeactivated?: boolean;
    itemRightSection?: (item: UserRecord) => React.ReactNode;
    onClear: () => void;
    rememberSortPreference?: boolean;
    sortPreferenceKey?: string;
}
type PickerProps = Pick<ComponentPropsWithoutRef<typeof Picker>, 'minimizeHeight' | 'height' | 'disableAutoFocus' | 'controlled'>;
export function UserPicker(props: IUserPickerProps & PickerProps) {
    const {
        excludeCurrentUser,
        includeDeactivated,
        itemRightSection,
        onChange,
        selectedUserIds,
        onClear,
        rememberSortPreference,
        sortPreferenceKey,
        ...pickerProps
    } = props;

    const userPrefs = rememberSortPreference
        ? useGlobalUserPrefs<{ sortField: 'name' | 'email' | 'selected'; sortDirection: 'asc' | 'desc' }>(sortPreferenceKey || 'UserPickerSort', {
              sortField: 'name',
              sortDirection: 'asc',
          })
        : null;

    const [sortField, setSortField] = useState<'name' | 'email' | 'selected'>(userPrefs?.get().sortField || 'name');
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>(userPrefs?.get().sortDirection || 'asc');

    const { loading, userList } = useCompanyUserList();
    const currentUser = useCurrentUser();

    const selectedUsers = useMemo(() => {
        return [...new Set(selectedUserIds)].map((id) => userList?.getById(id)!).filter((u) => !!u);
    }, [loading, JSON.stringify(props.selectedUserIds)]);

    const nameAccessor = useCallback((user: UserRecord) => [user.EMail ?? '', user.fullName, user.formalSortableName], []);
    const handleChange = useCallback(
        (users: UserRecord[]) => {
            onChange(users.map((u) => u.Id ?? 0).filter((id) => !!id));
        },
        [onChange]
    );

    const filter = useCallback((item: UserRecord) => {
        return (!excludeCurrentUser || currentUser?.Id !== item.Id) && (includeDeactivated || item.Status !== 'Deactivated');
    }, []);
    const { floatOnMouseEnter } = useFloatedFullText({ background: 'white', borderRadius: '3px' });
    const itemRenderer = useCallback(
        (user: UserRecord) => {
            return (
                <UserItemLayout data-userpicker-component="user-item-layout">
                    <UserImage
                        firstName={user.FirstName}
                        lastName={user.LastName}
                        email={user.EMail}
                        picture={user.Picture ?? ''}
                        id={user?.Id ?? 0}
                        diameter={28}
                    />
                    <Box data-userpicker-component="user-info">
                        <Text component={EllipsisTextEl} size={13} onMouseEnter={floatOnMouseEnter} data-userpicker-component="user-name">
                            <Text component="span" color="error" size="xs">
                                {user.Status === 'Deactivated' ? '(deactivated user)' : ''}
                            </Text>
                            {`${user?.FirstName} ${user?.LastName}`}{' '}
                        </Text>
                        <Text component={EllipsisTextEl} onMouseEnter={floatOnMouseEnter} size="xs">
                            {user?.EMail}
                        </Text>
                    </Box>
                    <Box data-userpicker-component="right-section">{itemRightSection?.(user)}</Box>
                </UserItemLayout>
            );
        },
        [itemRightSection]
    );

    const setSort = useCallback(
        ({ sortBy, sortDir }: { sortBy: typeof sortField; sortDir: typeof sortDirection }) => {
            setSortField(sortBy);
            setSortDirection(sortDir);

            if (rememberSortPreference && userPrefs) {
                userPrefs.set({ sortField: sortBy, sortDirection: sortDir });
            }
        },

        [setSortField, setSortDirection, rememberSortPreference, userPrefs]
    );

    const sortOptions: OptionMenuButton[] = useMemo(() => {
        const options: {
            label: string;
            sortBy: 'name' | 'email' | 'selected';
            sortDir: 'asc' | 'desc';
            icon: React.ElementType;
            tooltipLabel: string;
        }[] = [
            { label: 'Name A to Z', sortBy: 'name', sortDir: 'asc', icon: SortAscendingLetters, tooltipLabel: 'Sorted By Name A to Z' },
            { label: 'Name Z to A', sortBy: 'name', sortDir: 'desc', icon: SortDescendingLetters, tooltipLabel: 'Sorted By Name Z to A' },
            { label: 'Email A to Z', sortBy: 'email', sortDir: 'asc', icon: SortAscendingLetters, tooltipLabel: 'Sorted By Email A to Z' },
            { label: 'Email Z to A', sortBy: 'email', sortDir: 'desc', icon: SortDescendingLetters, tooltipLabel: 'Sorted By Email Z to A' },
            { label: 'Selection First', sortBy: 'selected', sortDir: 'asc', icon: SortAscending2, tooltipLabel: 'Sorted By Selection First' },
            { label: 'Selection Last', sortBy: 'selected', sortDir: 'desc', icon: SortDescending2, tooltipLabel: 'Sorted By Selection Last' },
        ];

        return options.map(({ icon: Icon, label, sortBy, sortDir }) => ({
            label: `Sort by ${label}`,
            icon: <Icon size={16} />,
            onClick: () => setSort({ sortBy, sortDir }),
            selected: sortField === sortBy && sortDirection === sortDir,
        }));
    }, [sortField, sortDirection, setSort]);

    const sortedItems = useMemo(() => {
        const items = userList?.getList().filter(filter) ?? [];

        const fieldAccessor = (user: UserRecord): string | boolean => {
            switch (sortField) {
                case 'name':
                    return user.formalSortableName?.toLowerCase() ?? '';
                case 'email':
                    return user.EMail?.toLowerCase() ?? '';
                case 'selected':
                    return selectedUserIds.includes(user.Id ?? -1);
                default:
                    return user.formalSortableName?.toLowerCase() ?? '';
            }
        };

        return [...items].sort((a, b) => {
            if (sortField === 'selected') {
                const aSel = fieldAccessor(a) as boolean;
                const bSel = fieldAccessor(b) as boolean;
                if (aSel !== bSel) {
                    return sortDirection === 'asc' ? (aSel ? -1 : 1) : aSel ? 1 : -1;
                }
                const aName = a.formalSortableName?.toLowerCase() ?? '';
                const bName = b.formalSortableName?.toLowerCase() ?? '';
                return aName.localeCompare(bName);
            }

            const aVal = fieldAccessor(a) as string;
            const bVal = fieldAccessor(b) as string;
            return sortDirection === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
        });
    }, [userList, filter, selectedUserIds, sortField, sortDirection]);

    const { label } = sortOptions.find((o) => o.selected) ?? sortOptions[0];
    const sortTooltip = typeof label === 'string' ? `Sorted by ${label.replace('Sort by', '')}` : 'Sorted';

    return (
        <FillerSwitch loading={loading} noData={!(userList?.getList() ?? []).filter(filter).length} height={150} noDataMessage="No users found">
            {() => (
                <>
                    <Picker
                        inlineFullText
                        items={[...sortedItems]}
                        itemHeight={46}
                        filter={filter}
                        nameAccessor={nameAccessor}
                        onChange={handleChange}
                        selections={selectedUsers}
                        renderItem={itemRenderer}
                        bubbleUpSelections={false}
                        {...pickerProps}
                        width="100%"
                        filterPlaceholder="Search users"
                        showSortMenu
                        sortOptions={sortOptions}
                        showClearButton
                        onClear={onClear}
                        sortTooltipLabel={sortTooltip}
                    />
                </>
            )}
        </FillerSwitch>
    );
}

const UserItemLayout = styled.div`
    display: grid;
    grid-template-columns: max-content auto min-content;
    align-items: center;
    gap: 6px;
    [data-userpicker-component='user-info'] {
        overflow: hidden;
    }
`;
